import { useState } from 'react';

export default function ExecutionPlan() {
  const [openPhase, setOpenPhase] = useState<number | null>(0);

  const phases = [
    {
      id: 1,
      name: 'الإعداد',
      color: 'bg-[#C8E6C9]',
      borderColor: 'border-[#1B5E20]',
      activities: [
        { id: 1, name: 'تشكيل اللجنة العليا والفرق التنفيذية.', weeks: [1] },
        { id: 2, name: 'اعتماد محاور الملتقى وأوراق العمل.', weeks: [2] },
        { id: 3, name: 'دعوة الجمعيات الشبابية والمتحدثين.', weeks: [3, 4] },
        { id: 4, name: 'إعداد الهوية البصرية والخطة الإعلامية.', weeks: [3, 4] },
        { id: 5, name: 'فتح باب التسجيل وإدارة المشاركات.', weeks: [5] },
        { id: 6, name: 'اعداد منصة خاصة بالملتقى تفاعلية.', weeks: [2, 3, 4, 5, 6, 7, 8] },
      ]
    },
    {
      id: 2,
      name: 'التنفيذ (اليوم الأول)',
      color: 'bg-[#A5D6A7]',
      borderColor: 'border-[#2E7D32]',
      activities: [
        { id: 7, name: 'الجلسة الافتتاحية', weeks: [9] },
        { id: 8, name: 'تدشين منصة وثيق "مركز معلومات الشباب"', weeks: [9] },
        { id: 9, name: 'أوراق العمل الرئيسة', weeks: [9] },
        { id: 10, name: 'ورشتا عمل تطبيقية', weeks: [9] },
        { id: 11, name: 'افتتاح المعرض الشبابي', weeks: [9] },
      ]
    },
    {
      id: 3,
      name: 'التنفيذ (اليوم الثاني)',
      color: 'bg-[#81C784]',
      borderColor: 'border-[#388E3C]',
      activities: [
        { id: 12, name: 'أوراق عمل تخصصية', weeks: [9] },
        { id: 13, name: 'ورشتا عمل تطبيقية', weeks: [9] },
        { id: 14, name: 'جلسة التوصيات وصياغة المخرجات', weeks: [9] },
        { id: 15, name: 'الختام', weeks: [9] },
      ]
    },
    {
      id: 4,
      name: 'الإغلاق',
      color: 'bg-[#E8F5E9]',
      borderColor: 'border-[#4CAF50]',
      activities: [
        { id: 16, name: 'تحليل مخرجات الورش.', weeks: [10] },
        { id: 17, name: 'إعداد التقرير الختامي.', weeks: [10] },
        { id: 18, name: 'نشر التوصيات ومؤشر الاحتياجات.', weeks: [11] },
        { id: 19, name: 'تقييم الأداء ورفع التقارير للرعاة.', weeks: [12] },
      ]
    }
  ];

  const weeks = Array.from({ length: 12 }, (_, i) => i + 1);

  const getBarColor = (phaseId: number) => {
    switch (phaseId) {
      case 1: return 'bg-[#81C784]';
      case 2: return 'bg-[#66BB6A]';
      case 3: return 'bg-[#4CAF50]';
      case 4: return 'bg-[#A5D6A7]';
      default: return 'bg-[#81C784]';
    }
  };

  return (
    <section id="execution-plan" className="py-10 md:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#1B5E20] flex items-center justify-center">
              <i className="ri-calendar-schedule-line text-xl md:text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B5E20]">مراحل وخطة التنفيذ التفصيلية</h2>
          </div>
          
          {/* Duration Info */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-6">
            <div className="bg-white rounded-xl px-6 py-3 shadow-md border border-[#1B5E20]/20">
              <span className="text-gray-600 text-sm">مدة المشروع:</span>
              <span className="text-[#1B5E20] font-bold text-lg mr-2">3 أشهر</span>
            </div>
            <div className="bg-white rounded-xl px-6 py-3 shadow-md border border-[#D4AF37]/20">
              <span className="text-gray-600 text-sm">مدة الملتقى:</span>
              <span className="text-[#D4AF37] font-bold text-lg mr-2">يومان كاملان</span>
            </div>
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="md:hidden space-y-4">
          {phases.map((phase) => (
            <div key={phase.id} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              {/* Phase Header */}
              <div className="bg-gradient-to-l from-[#1B5E20] to-[#2E7D32] px-4 py-3 flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${getBarColor(phase.id)}`}></div>
                <h3 className="text-white font-bold text-base">{phase.name}</h3>
              </div>
              {/* Activities */}
              <div className="divide-y divide-gray-100">
                {phase.activities.map((activity) => (
                  <div key={activity.id} className="px-4 py-3">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-xs font-bold text-white bg-[#1B5E20] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">{activity.id}</span>
                      <p className="text-sm text-gray-700 leading-relaxed">{activity.name}</p>
                    </div>
                    {/* Week bar */}
                    <div className="flex gap-0.5 mt-2 mr-7">
                      {weeks.map(week => (
                        <div
                          key={week}
                          className={`flex-1 h-4 rounded-sm ${activity.weeks.includes(week) ? getBarColor(phase.id) : 'bg-gray-100'}`}
                          title={`أسبوع ${week}`}
                        ></div>
                      ))}
                    </div>
                    <div className="flex gap-0.5 mr-7 mt-0.5">
                      {weeks.map(week => (
                        <div key={week} className="flex-1 text-center text-[9px] text-gray-400">{week}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Gantt Chart Table */}
        <div className="hidden md:block bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead>
                <tr className="bg-gradient-to-l from-[#1B5E20] to-[#2E7D32]">
                  <th className="text-white py-4 px-3 text-center text-sm font-bold w-12 border-l border-white/20">م</th>
                  <th className="text-white py-4 px-3 text-center text-sm font-bold w-32 border-l border-white/20">المرحلة</th>
                  <th className="text-white py-4 px-3 text-right text-sm font-bold min-w-[250px] border-l border-white/20">النشاط</th>
                  <th className="text-white py-4 px-2 text-center text-sm font-bold" colSpan={12}>
                    <div className="mb-2">الأسابيع</div>
                  </th>
                </tr>
                <tr className="bg-gradient-to-l from-[#1B5E20] to-[#2E7D32]">
                  <th colSpan={3} className="border-l border-white/20"></th>
                  {weeks.map(week => (
                    <th key={week} className="text-white py-2 px-0 text-center text-xs font-medium w-[60px] border-l border-white/20">{week}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {phases.map((phase) => (
                  phase.activities.map((activity, actIndex) => (
                    <tr 
                      key={activity.id} 
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${actIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                    >
                      <td className="py-3 px-3 text-center text-sm font-semibold text-gray-700 border-l border-gray-200">{activity.id}</td>
                      <td className={`py-3 px-3 text-center text-sm font-bold border-l border-gray-200 ${actIndex === 0 ? 'text-[#1B5E20]' : 'text-transparent'}`}>
                        {actIndex === 0 ? phase.name : phase.name}
                      </td>
                      <td className="py-3 px-3 text-right text-sm text-gray-700 border-l border-gray-200">{activity.name}</td>
                      {weeks.map(week => (
                        <td key={week} className="py-3 px-0 border-l border-gray-200 w-[60px]">
                          <div className="flex items-center justify-center h-6">
                            {activity.weeks.includes(week) && (
                              <div className={`w-[50px] h-5 ${getBarColor(phase.id)} rounded-sm shadow-sm`}></div>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-12 bg-white rounded-xl p-6 shadow-md">
          <h4 className="text-lg font-bold text-[#1B5E20] mb-4 text-center">دليل الألوان</h4>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {phases.map(phase => (
              <div key={phase.id} className="flex items-center gap-2">
                <div className={`w-6 h-4 ${getBarColor(phase.id)} rounded`}></div>
                <span className="text-sm text-gray-700">{phase.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
